import "./SignaturePreviewModalComponent.scss";
import React, {useEffect, useState, useRef, useCallback} from "react";
import {Subscription} from "rxjs";
import {CommonService} from "../../services";
import DialogComponent from "../dialog/dialogComponent";
import ButtonComponent from "../core/button/ButtonComponent";
import SignatureV2Component from "../signature-v2/SignatureV2Component";
import {Add} from "@mui/icons-material";
import {ImageConfig} from "../../../constants";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {IAPIResponseType} from "../../models/api.model";
import {setUserSignature} from "../../../store/actions/account.action";

interface SignaturePreviewModalComponentProps {
}

const SignaturePreviewModalComponent = (
    props: SignaturePreviewModalComponentProps
) => {
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [isSignaturePreviewOpen, setIsSignaturePreviewOpen] = useState(false);
    const [isSignatureUpdateModalOpen, setIsSignatureUpdateModalOpen] =
        useState(false);
    const signaturePreviewSubscriptionRef = useRef<Subscription>();
    const [appliedSignature, setAppliedSignature] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSignatureModified, setSignatureModified] = useState(false);
    const dispatch = useDispatch();

    const signature = useSelector(
        (state: IRootReducerState) => state.account.userSignature
    );

    // const labConfiguration = useSelector(
    //     (state: IRootReducerState) =>
    //         state?.staticData?.systemConfig?.data?.labConfiguration
    // );

    const handleSignatureUpdate = useCallback(() => {
        if (signature && signature?.signatureCount > 3) {
            CommonService.openConfirmationDialog({
                confirmationTitle: "Please Confirm !",
                confirmationSubTitle: "You have already updated the signature 3 times. Are you sure you still want to update it?",
                disableEscKeyClose: false,
                disableBackdropClose: false,
                imagePath: `${ImageConfig.LogoutImage}`,
                direction: "up",
                yes: {
                    text: "Proceed",
                    color: "primary"
                },
                no: {
                    text: "Cancel",
                    color: "primary"
                }
            })
                .then(() => {
                    setIsSignatureUpdateModalOpen(true)

                })
                .catch(() => {
                })
        } else {
            setIsSignatureUpdateModalOpen(true)
        }
    }, [signature]);

    const storeSignature = useCallback(
        (value: {
            type: string;
            signature: string | null;
            font?: string;
            color?: string;
        }) => {
            setAppliedSignature(value.signature);
            setIsSignatureUpdateModalOpen(false);
            setSignatureModified(true);
        },
        []
    );

    const handleProceedSignature = useCallback(() => {
        setIsLoading(true);
        CommonService._account
            .AddSignature(currentUser?.id, {signature: appliedSignature})
            .then((response: IAPIResponseType<any>) => {
                dispatch(setUserSignature(response.data));
                setSignatureModified(false);
                setIsSignaturePreviewOpen(false);
                CommonService._alert.showToast(response.message || "Updated signature successfully", 'success')
            }).catch((err) => {
            CommonService._alert.showToast(err?.error || "Failed to update signature", 'error')
        })
            .finally(() => {
                setIsLoading(false);
            });
    }, [appliedSignature, currentUser?.id, dispatch]);

    useEffect(() => {
        signaturePreviewSubscriptionRef.current =
            CommonService._communications.SignaturePreviewSubject.subscribe(
                (isOpen: boolean) => {
                    setIsSignaturePreviewOpen(isOpen);
                }
            );

        return () => {
            signaturePreviewSubscriptionRef.current?.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (signature?.signature) {
            setAppliedSignature(signature?.signature);
        }
    }, [signature?.signature]);

    return (
        <>
            <DialogComponent
                isShowCancel={true}
                fullWidth={true}
                maxWidth="md"
                onClose={() => setIsSignaturePreviewOpen(false)}
                title={"View Signature:"}
                description={"To comply with medical standards and ensure operational accuracy, your signature is required to complete the onboarding process. This is mandatory for maintaining compliance with medical regulations."}
                className="signature-preview-modal-component"
                open={isSignaturePreviewOpen}
            >
                <div className="signature-preview-card">
                    <div className="signature-preview-card-body">
                        <div className="signature-preview-card-body-header">
                            Signature:
                        </div>
                        {!appliedSignature ? (
                            <ButtonComponent
                                startIcon={<Add/>}
                                variant="outlined"
                                handleClick={() => setIsSignatureUpdateModalOpen(true)}
                            >
                                Add Signature
                            </ButtonComponent>
                        ) : (
                            <div className="signature-box-container">
                                <div
                                    className="signature-box"
                                    style={{backgroundImage: `url(${appliedSignature})`}}
                                />
                            </div>
                        )}
                    </div>
                    <div className="signature-preview-card-footer">
                        <ButtonComponent
                            variant="outlined"
                            disabled={!appliedSignature}
                            handleClick={() => handleSignatureUpdate()}
                        >
                            Edit Signature
                        </ButtonComponent>
                        <ButtonComponent
                            variant="contained"
                            isLoading={isLoading}
                            disabled={!appliedSignature || !isSignatureModified}
                            handleClick={handleProceedSignature}
                        >
                            Save
                        </ButtonComponent>
                    </div>
                </div>
            </DialogComponent>
            <DialogComponent
                isShowCancel={true}
                fullWidth={true}
                onClose={() => setIsSignatureUpdateModalOpen(false)}
                title={"Add Signature:"}
                open={isSignatureUpdateModalOpen}
            >
                {isSignatureUpdateModalOpen && (
                    <SignatureV2Component onSave={storeSignature}/>
                )}
            </DialogComponent>
        </>
    );
};

export default SignaturePreviewModalComponent;
